/**
 * Email Templates Zustand Store
 *
 * Manages all state for the Super Admin Email Template Editor including:
 * - Template list with customization status
 * - Editor state for the selected template
 * - CRUD operations: save, reset, test-send
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  EmailTemplateKey,
  EmailTemplateListItem,
  EmailTemplateDetail,
  UpdateEmailTemplateDto,
} from '@respectly/shared-types';

// ============================================
// State Interface
// ============================================

export interface EmailTemplatesState {
  // List
  templates: EmailTemplateListItem[];
  isLoadingList: boolean;
  listError: string | null;

  // Editor (selected template detail)
  selectedKey: EmailTemplateKey | null;
  editorTemplate: EmailTemplateDetail | null;
  isLoadingDetail: boolean;
  detailError: string | null;

  // Action states
  isSaving: boolean;
  isResetting: boolean;
  isSendingTest: boolean;
  actionError: string | null;

  // Actions
  loadTemplates: () => Promise<void>;
  loadTemplate: (key: EmailTemplateKey) => Promise<void>;
  saveTemplate: (key: EmailTemplateKey, dto: UpdateEmailTemplateDto) => Promise<boolean>;
  resetTemplate: (key: EmailTemplateKey) => Promise<boolean>;
  sendTestEmail: (key: EmailTemplateKey, to: string) => Promise<boolean>;
  setSelectedKey: (key: EmailTemplateKey | null) => void;
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const getInitialState = () => ({
  templates: [] as EmailTemplateListItem[],
  isLoadingList: false,
  listError: null,

  selectedKey: null as EmailTemplateKey | null,
  editorTemplate: null as EmailTemplateDetail | null,
  isLoadingDetail: false,
  detailError: null,

  isSaving: false,
  isResetting: false,
  isSendingTest: false,
  actionError: null,
});

// ============================================
// Store
// ============================================

export const useEmailTemplatesStore = create<EmailTemplatesState>((set, get) => ({
  ...getInitialState(),

  /**
   * Load the list of all 13 email template definitions with customization status.
   */
  loadTemplates: async () => {
    set({ isLoadingList: true, listError: null });
    try {
      const api = getApiClient();
      const data = await api.get<EmailTemplateListItem[]>(
        API_ENDPOINTS.SUPER_ADMIN.EMAIL_TEMPLATES.LIST
      );
      set({ templates: data, isLoadingList: false });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load email templates';
      set({ isLoadingList: false, listError: message });
    }
  },

  /**
   * Load a single template for editing (DB override or rendered static default).
   */
  loadTemplate: async (key: EmailTemplateKey) => {
    set({ selectedKey: key, isLoadingDetail: true, detailError: null, editorTemplate: null });
    try {
      const api = getApiClient();
      const data = await api.get<EmailTemplateDetail>(
        API_ENDPOINTS.SUPER_ADMIN.EMAIL_TEMPLATES.GET(key)
      );
      set({ editorTemplate: data, isLoadingDetail: false });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load template';
      set({ isLoadingDetail: false, detailError: message });
    }
  },

  /**
   * Save (create or update) a custom template override.
   */
  saveTemplate: async (key: EmailTemplateKey, dto: UpdateEmailTemplateDto) => {
    set({ isSaving: true, actionError: null });
    try {
      const api = getApiClient();
      await api.put(API_ENDPOINTS.SUPER_ADMIN.EMAIL_TEMPLATES.UPDATE(key), dto);

      // Update the list item's isCustomized flag
      set((state) => ({
        isSaving: false,
        templates: state.templates.map((t) =>
          t.key === key
            ? { ...t, isCustomized: true, customSubject: dto.subject, preheader: dto.preheader ?? null }
            : t
        ),
        editorTemplate: state.editorTemplate
          ? { ...state.editorTemplate, isCustomized: true, subject: dto.subject, htmlContent: dto.htmlContent, preheader: dto.preheader ?? null }
          : state.editorTemplate,
      }));
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save template';
      set({ isSaving: false, actionError: message });
      return false;
    }
  },

  /**
   * Reset a template to its static default (deletes the DB override).
   */
  resetTemplate: async (key: EmailTemplateKey) => {
    set({ isResetting: true, actionError: null });
    try {
      const api = getApiClient();
      await api.post(API_ENDPOINTS.SUPER_ADMIN.EMAIL_TEMPLATES.RESET(key), {});

      // Reload the template so the editor shows the static default HTML
      await get().loadTemplate(key);

      set((state) => ({
        isResetting: false,
        templates: state.templates.map((t) =>
          t.key === key ? { ...t, isCustomized: false, customSubject: undefined, preheader: null, updatedAt: undefined } : t
        ),
      }));
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to reset template';
      set({ isResetting: false, actionError: message });
      return false;
    }
  },

  /**
   * Send a test email with sample data to the specified address.
   */
  sendTestEmail: async (key: EmailTemplateKey, to: string) => {
    set({ isSendingTest: true, actionError: null });
    try {
      const api = getApiClient();
      await api.post(API_ENDPOINTS.SUPER_ADMIN.EMAIL_TEMPLATES.TEST_SEND(key), { to });
      set({ isSendingTest: false });
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to send test email';
      set({ isSendingTest: false, actionError: message });
      return false;
    }
  },

  setSelectedKey: (key) => set({ selectedKey: key }),

  clearError: () => set({ listError: null, detailError: null, actionError: null }),

  reset: () => set(getInitialState()),
}));

// ============================================
// Selector Hooks
// ============================================

export const useEmailTemplateByKey = (key: EmailTemplateKey) =>
  useEmailTemplatesStore((s) => s.templates.find((t) => t.key === key));

export const useCustomizedTemplateCount = () =>
  useEmailTemplatesStore((s) => s.templates.filter((t) => t.isCustomized).length);

export const useEmailTemplatesByCategory = (category: string) =>
  useEmailTemplatesStore((s) =>
    category === 'all' ? s.templates : s.templates.filter((t) => t.category === category)
  );

export const useEmailTemplatesLoading = () =>
  useEmailTemplatesStore((s) => s.isLoadingList);
